import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, CircularProgress, Typography, alpha } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import { ApartmentOutlined } from '@mui/icons-material';
import Switch from '@mui/material/Switch';
import { api } from '../../services/api';
import { useToast } from "../../components/ToastContext";
import SearchComponent from '../../components/Search';
import { filterRowsByColumns } from '../../Utils/gridSearch';
import StatusFilterHeader, { applyStatusFilter } from '../../components/StatusFilterHeader';
import AddNewClient from './addNewClient';

const Organizations = () => {
  const theme = useTheme();
  const toast = useToast();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedClientData, setSelectedClientData] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const userId = sessionStorage.getItem('userId');
      const response = await api.get(`/Client/GetOrganizationDetailsByUserId/${userId}`);
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching client data:', error);
      toast.error(error.message || 'Failed to fetch client data.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedClientData(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (clientRow) => {
    setSelectedClientData(clientRow);
    setIsDialogOpen(true);
  };

  const handleDialogSuccess = () => {
    setIsDialogOpen(false);
    fetchClients();
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

const handleStatusChange = async (client) => {
    const updatedStatus = !client.isActive;
    const originalStatus = client.isActive;

    try {
      setClients(prevClients => prevClients.map(c =>
        c.organizationId === client.organizationId ? { ...c, isActive: updatedStatus } : c
      ));

      const userId = sessionStorage.getItem('userId');
      const payload = {
        organizationName: String(client.organizationName),
        organizationLocation: Number.parseInt(client.locationId, 10),
        timeZone: String(client.timeZoneID),
        requesterId: Number.parseInt(userId, 10),
        isActive: updatedStatus,
      };

      const response = await api.post(`/Client/UpdateOrganizationDetails/${client.organizationId}`, payload);

      if (response.status === 200) {
        toast.success(`Organization ${updatedStatus ? 'activated' : 'deactivated'} successfully.`);
      } else {
        throw new Error('Failed to update status.');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      
      const apiErrorMessage = error.response?.data?.errorMessages?.[0]?.message;

      if (apiErrorMessage) {
        toast.error(apiErrorMessage);
      } else {
        toast.error(error.message || 'Failed to update status.');
      }

      setClients(prevClients => prevClients.map(c =>
        c.organizationId === client.organizationId ? { ...c, isActive: originalStatus } : c
      ));
    }
  };

  const columns = useMemo(() => [
    { field: 'clientName', headerName: 'Client Name', flex: 1 },
    { field: 'organizationName', headerName: 'Organization Name', flex: 1 },
    { field: 'location', headerName: 'Location', flex: 1 },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // Expose a searchable label so search matches "active"/"inactive"
      valueGetter: (value, row) => (row?.isActive ? 'Active' : 'Inactive'),
      renderHeader: () => (
        <StatusFilterHeader value={statusFilter} onChange={setStatusFilter} />
      ),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={params.row.isActive}
            onChange={() => handleStatusChange(params.row)}
            color="primary"
          />
          <Box sx={{ paddingRight: 2, fontWeight: 600, color: params.row.isActive ? '#388e3c' : '#d32f2f' }}>
            {params.row.isActive ? 'Active' : 'Inactive'}
          </Box>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleEdit(params.row)}
          sx={{ mt: 0.9, borderRadius: 999, px: 1.8 }}
        >
          Edit
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [theme, statusFilter]);

  // Search across all data columns, then apply the Status dropdown filter
  const filteredClients = useMemo(
    () => applyStatusFilter(filterRowsByColumns(clients, searchTerm, columns), statusFilter),
    [clients, searchTerm, columns, statusFilter]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1), color: theme.palette.primary.main }}>
            <ApartmentOutlined />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary' }}>
            Organizations
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SearchComponent
            value={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder="Search clients..."
          />
          <Button
            variant="contained"
            sx={{
              color: theme.palette.common.white,
              bgcolor: theme.palette.primary.main,
              borderRadius: 2,
              px: 2.4,
              py: 1.1,
            }}
            onClick={handleAddNew}
          >
            Add New Organization
          </Button>
        </Box>
      </Box>

      <Box sx={{ height: 'calc(100vh - 310px)', minHeight: 380, width: '100%' }}>
        {loading ? (
          <Box sx={{ minHeight: 280, display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredClients}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
            hideFooterSelectedRowCount
            getRowId={(row) => row.organizationId}
            sx={{
              border: "none",
              bgcolor: (t) => t.palette.mode === "dark" ? "background.paper" : "#ffffff",

              "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root, & .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiSvgIcon-root, & .MuiCheckbox-root.Mui-indeterminate .MuiSvgIcon-root": {
                color: (t) => `${t.palette.primary.main} !important`,
              },

              "& .MuiCheckbox-root .MuiSvgIcon-root": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)",
              },

              "& .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root": {
                color: (t) => `${t.palette.action.disabled} !important`,
              },

              "--DataGrid-rowBorderColor": "transparent",

              "& .MuiDataGrid-filler": { display: "none" },

              "& .MuiDataGrid-columnHeaders": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                color: "text.primary",
                borderBottom: "none",
              },

              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold !important",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              },

              "& .MuiDataGrid-columnSeparator": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-columnHeader .MuiSvgIcon-root:not(.MuiCheckbox-root .MuiSvgIcon-root)": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-cell": {
                color: "text.primary",
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-row:nth-of-type(even)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "#fafafa",
              },

              "& .MuiDataGrid-row:nth-of-type(odd)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "transparent" : "#ffffff",
              },

              "& .MuiDataGrid-row:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08) !important" : "#e8f0fe !important",
              },

              "& .MuiDataGrid-row.Mui-selected": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.16) !important" : "#e3f2fd !important",
              },

              "& .MuiDataGrid-row.Mui-selected:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.24) !important" : "#d0eafb !important",
              },

              "& .MuiDataGrid-footerContainer": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                borderTop: "none",
                color: "text.primary",
              },

              "& .floating-actions, & .row-actions": {
                backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(40, 40, 40, 0.9)" : "#e8f0fe",
                flex: 1,
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                gap: "6px",
                opacity: 0,
                pointerEvents: "none",
                transition: "opacity 0.2s ease-in-out",
                padding: "4px 8px",
                borderRadius: "4px",
              },

              "& .MuiDataGrid-row:hover .floating-actions, & .MuiDataGrid-row:hover .row-actions, & .MuiDataGrid-row.Mui-selected .floating-actions, & .MuiDataGrid-row.Mui-selected .row-actions": {
                opacity: 1,
                pointerEvents: "auto",
              },

              "& .MuiDataGrid-cell[data-field='__floating_actions'], & .MuiDataGrid-cell[data-field='actions']": {
                overflow: "visible !important",
                width: "0px !important",
                padding: 0,
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                outline: "none"
              },
            }}
          />
        )}
      </Box>

      {/* Reusable External Popup Component */}
      <AddNewClient
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        clientData={selectedClientData}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default Organizations;